import { createLogicGraph, getEventNodesForObject } from './logicGraph.js';
import { createLogicNode } from './logicNodeFactory.js';

// Default node footprint used for automatic layout (page-local pixels).
export const LOGIC_NODE_SIZE = Object.freeze({ width: 180, height: 44 });
const ACTION_COLUMN_OFFSET = 220;
const ROW_GAP = 12;

/**
 * Convert stored per-object handlers (`node.actions`) into event/action nodes.
 *
 * `objects` is `[{ uuid, handlers, y }]` in a stable order; handler and action
 * ids become node ids (prefixed only on collision), so migrating the same
 * data twice yields the same graph and compiling the result reproduces the
 * original handlers, ids included.
 * Layout: event nodes in a column at `originX`, aligned with their object and
 * never overlapping; their actions in a second column.
 */
export function migrateHandlersToLogic(objects, { graph = createLogicGraph(), originX = 0 } = {}) {
    const result = { ...graph, nodes: [...graph.nodes], wires: [...graph.wires] };
    const rowHeight = LOGIC_NODE_SIZE.height + ROW_GAP;
    const usedIds = new Set(result.nodes.map(node => node.id));
    const nodeId = (id, prefix) => {
        const candidate = id && !usedIds.has(id) ? id : `${prefix}-${id ?? usedIds.size}`;
        usedIds.add(candidate);
        return candidate;
    };
    let cursorY = -Infinity;

    for (const { uuid, handlers, y = 0 } of objects) {
        let rowY = Math.max(y, cursorY);
        for (const handler of handlers ?? []) {
            const existing = handler.event
                ? getEventNodesForObject(result, uuid).find(node => node.data.event === handler.event)
                : null;
            let eventNode = existing;
            if (!eventNode) {
                eventNode = createLogicNode('event', {
                    id: nodeId(handler.id, 'ev'), x: originX, y: rowY, preset: { event: handler.event ?? '' },
                });
                result.nodes.push(eventNode);
                result.wires.push({
                    id: `w-${eventNode.id}-source`,
                    from: { object: uuid, port: 'events' },
                    to: { node: eventNode.id, port: 'source' },
                });
            }
            let order = result.wires.filter(wire => wire.from.node === eventNode.id).length;
            (handler.actions ?? []).forEach(action => {
                const data = structuredClone(action);
                delete data.id;
                const actionNode = {
                    id: nodeId(action.id, 'ac'),
                    kind: 'action',
                    type: data.type,
                    x: originX + ACTION_COLUMN_OFFSET,
                    y: eventNode.y + order * rowHeight,
                    data,
                };
                result.nodes.push(actionNode);
                result.wires.push({
                    id: `w-${eventNode.id}-${actionNode.id}`,
                    from: { node: eventNode.id, port: 'actions' },
                    to: { node: actionNode.id, port: 'trigger' },
                    order: order++,
                });
            });
            if (!existing) rowY += Math.max(1, handler.actions?.length ?? 0) * rowHeight;
        }
        cursorY = rowY;
    }
    return result;
}
